import { Link } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState, NoDataIcon } from '@/components/ui/EmptyState';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/Table';
import { PnlDisplay } from '@/components/ui/PnlDisplay';
import { useSymbolPerformance } from '@/hooks/queries/useAnalytics';
import { formatCurrency } from '@/lib/formatters';

export function SymbolBreakdown() {
  const { data: symbols, isLoading } = useSymbolPerformance();

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Performance by Symbol</CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center py-8">
          <Spinner />
        </CardContent>
      </Card>
    );
  }

  if (!symbols || symbols.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Performance by Symbol</CardTitle>
        </CardHeader>
        <CardContent>
          <EmptyState
            icon={<NoDataIcon />}
            title="No data yet"
            description="Trade some symbols to see the breakdown"
          />
        </CardContent>
      </Card>
    );
  }

  const topSymbols = symbols.slice(0, 10);
  const chartData = topSymbols.map((s) => ({
    symbol: s.symbol,
    pnl: s.totalPnl,
    trades: s.tradesCount,
  }));

  return (
    <Card>
      <CardHeader>
        <CardTitle>Performance by Symbol</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Chart */}
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} layout="vertical" margin={{ left: 50, right: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
                <XAxis
                  type="number"
                  tickFormatter={(value) => formatCurrency(value).replace('.00', '')}
                  stroke="#9ca3af"
                  fontSize={12}
                />
                <YAxis
                  type="category"
                  dataKey="symbol"
                  stroke="#9ca3af"
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip
                  content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const data = payload[0].payload;
                    return (
                      <div className="bg-white shadow-lg rounded-lg p-3 border border-gray-200">
                        <p className="font-semibold">{data.symbol}</p>
                        <p className="text-sm text-gray-600">{data.trades} trades</p>
                        <p className="text-sm">
                          P&L:{' '}
                          <span className={data.pnl >= 0 ? 'text-green-600' : 'text-red-600'}>
                            {formatCurrency(data.pnl)}
                          </span>
                        </p>
                      </div>
                    );
                  }}
                />
                <Bar dataKey="pnl" radius={[0, 4, 4, 0]}>
                  {chartData.map((entry, index) => (
                    <Cell
                      key={index}
                      fill={entry.pnl >= 0 ? '#10b981' : '#ef4444'}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Symbol</TableHead>
                  <TableHead className="text-center">Trades</TableHead>
                  <TableHead className="text-center">Open</TableHead>
                  <TableHead className="text-right">Collected</TableHead>
                  <TableHead className="text-right">Paid</TableHead>
                  <TableHead className="text-right">P&L</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {symbols.map((symbol) => (
                  <TableRow key={symbol.symbol}>
                    <TableCell>
                      <Link
                        to={`/symbols/${symbol.symbol}`}
                        className="font-semibold text-brand-600 hover:text-brand-700"
                      >
                        {symbol.symbol}
                      </Link>
                    </TableCell>
                    <TableCell className="text-center">{symbol.tradesCount}</TableCell>
                    <TableCell className="text-center">{symbol.openCount}</TableCell>
                    <TableCell className="text-right text-green-600">
                      {formatCurrency(symbol.premiumCollected)}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatCurrency(symbol.premiumPaid)}
                    </TableCell>
                    <TableCell className="text-right">
                      <PnlDisplay value={symbol.totalPnl} size="sm" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
